import { Client as BelfastClient } from '@kaizenplatform/belfast';
import { createBrowserHistory } from 'history';
import * as NewRelicBrowser from 'new-relic-browser';
import { createElement } from 'react';
import { hydrate, render } from 'react-dom';
import { addLocaleData } from 'react-intl';
import * as enLocaleData from 'react-intl/locale-data/en';
import * as jaLocaleData from 'react-intl/locale-data/ja';
import Store from 'repatch';

import GraphQLClient from './adapter/GraphQLClient';
import HttpClient from './adapter/HttpClient';
import Config from './core/Config';
import State from './core/State';
import baseInitialState from './initialState';
import createKukerEmitterMiddleware from './kukerEmitterMiddleware';
import { registerCustomElements } from './register-custom-elements';
import ApiErrorHandler from './service/ApiErrorHandler';
import ErrorPresenter from './service/ErrorPresenter';
import ErrorReporter from './service/ErrorReporter';
import IntercomService from './service/IntercomService';
import MessageDispatcher from './service/MessageDispatcher';
import AuthenticationWebApi from './service/persistent/AuthenticationWebApi';
import ConfigurationStorage from './service/persistent/ConfigurationStorage';
import CreationSetWebApi from './service/persistent/CreationSetWebApi';
import CreationWebApi from './service/persistent/CreationWebApi';
import DeliverableWebApi from './service/persistent/DeliverableWebApi';
import DownloadApi from './service/persistent/DownloadApi';
import FilesWebApi from './service/persistent/FilesWebApi';
import FileUploader from './service/persistent/FileUploader';
import ModificationRequestWebApi from './service/persistent/ModificationRequestWebApi';
import { AssignmentWebApi } from './service/persistent/offer/AssignmentWebApi';
import OfferWebApi from './service/persistent/offer/OfferWebApi';
import ProjectChangeWebApi from './service/persistent/ProjectChangeWebApi';
import ProjectWebApi from './service/persistent/ProjectWebApi';
import SearchAssetApi from './service/persistent/SearchAssetApi';
import SessionStorage from './service/persistent/SessionStorage';
import SessionWebApi from './service/persistent/SessionWebApi';
import UserWebApi from './service/persistent/UserWebApi';
import TranslationBackend from './service/TranslationBackend';
import setLocation from './thunk/setLocation';
import AuthenticationUsecase from './usecase/AuthenticationUsecase';
import ConfigurationChangingUsecase from './usecase/ConfigurationChangingUsecase';
import FatalErrorUsecase from './usecase/FatalErrorUsecase';
import ListOfferUsecase from './usecase/ListOfferUsecase';
import OfferApplicationUsecase from './usecase/OfferApplicationUsecase';
import SearchAssetUsecase from './usecase/SearchAssetUsecase';
import ShowOfferUsecase from './usecase/ShowOfferUsecase';
import ShowProjectUsecase from './usecase/ShowProjectUsecase';
import execSitejs from './utility/execSitejs';
import Root from './view/Root';

declare global {
  interface Window {
    newrelic: typeof NewRelicBrowser;
  }
}

const main = () => {
  registerCustomElements(window.customElements);

  addLocaleData([...enLocaleData, ...jaLocaleData, { locale: 'ja-JP', parentLocale: 'ja' } as any]);

  const config: Config = (window as any).__hugotconfig__;
  const appVersion: string = (window as any).__hugotversion__;

  const initialState = {
    ...baseInitialState,
    location: {
      pathname: window.location.pathname,
      search: window.location.search,
    },
  };
  const history = createBrowserHistory();
  const store = new Store<State>(initialState);

  const belfastClient = new BelfastClient({
    endpoint: config.BELFAST_API_BASE_URL,
  });
  const graphqlClient = new GraphQLClient({
    endpointUrl: config.HUGOT_GRAPHQL_API_ENDPOINT_URL,
  });
  const coreApiHttpClient = new HttpClient({
    baseUrl: config.CORE_API_BASE_URL,
  });
  const hugotApiHttpClient = new HttpClient({
    baseUrl: config.HUGOT_API_SERVER_URL,
  });
  const offerGraphqlClient = new GraphQLClient({
    endpointUrl: config.OFFER_GRAPHQL_API_ENDPOINT_URL,
  });

  const errorPresenter = new ErrorPresenter({ store });
  const errorReporter = new ErrorReporter({
    store,
    dsn: config.SENTRY_API_DSN,
    environment: config.STAGE,
    release: appVersion,
    history: history,
  });
  window.newrelic.addRelease(config.STAGE, appVersion);
  window.onunhandledrejection = (data: any) => {
    errorReporter.reportError(data.reason);
  };
  const apiErrorHandler = new ApiErrorHandler({ errorPresenter, errorReporter });

  const intercomService = new IntercomService(() => (window as any).Intercom, config.INTERCOM_APP_ID);
  const messageDispatcher = new MessageDispatcher({ store });

  const translationBackend = new TranslationBackend({ appVersion });
  const configurationStorage = new ConfigurationStorage({ storage: window.localStorage });

  const fileUploader = new FileUploader({ httpClient: hugotApiHttpClient });
  const downloadApi = new DownloadApi({ httpClient: hugotApiHttpClient });
  const projectWebApi = new ProjectWebApi({ graphqlClient });
  const projectChangeWebApi = new ProjectChangeWebApi({ graphqlClient });
  const creationSetWebApi = new CreationSetWebApi({ graphqlClient });
  const creationWebApi = new CreationWebApi({ graphqlClient });
  const deliverableWebApi = new DeliverableWebApi({ graphqlClient });
  const sessionStorage = new SessionStorage({ storage: window.localStorage });
  const sessionWebApi = new SessionWebApi({ httpClient: coreApiHttpClient });
  const userWebApi = new UserWebApi({ graphqlClient, httpClient: hugotApiHttpClient });
  const modificationRequestWebApi = new ModificationRequestWebApi({ graphqlClient });
  const authenticationWebApi = new AuthenticationWebApi({ httpClient: coreApiHttpClient });
  const searchAssetApi = new SearchAssetApi();

  const offerWebApi = new OfferWebApi({ offerGraphqlClient: offerGraphqlClient });
  const assignmentWebApi = new AssignmentWebApi({ graphqlClient: offerGraphqlClient });

  const filesWebApi = new FilesWebApi();

  const authenticationUsecase = new AuthenticationUsecase({
    store,
    errorPresenter,
    apiErrorHandler,
    authenticationWebApi,
    intercomService,
    sessionStorage,
    sessionWebApi,
    userWebApi,
  });
  const configurationChangingUsecase = new ConfigurationChangingUsecase({
    store,
    configurationStorage,
    translationBackend,
  });
  const showProjectUsecase = new ShowProjectUsecase({
    store,
    belfastClient,
    projectWebApi,
    downloadApi,
    errorPresenter,
    messageDispatcher,
    projectChangeWebApi,
    filesWebApi,
    hugotApiBaseUrl: config.HUGOT_API_SERVER_URL,
  });

  const showOfferUsecase = new ShowOfferUsecase({
    store,
    belfastClient,
    offerWebApi,
    errorPresenter,
    projectChangeWebApi,
    projectWebApi,
    filesWebApi,
  });
  const offerApplicationUsecase = new OfferApplicationUsecase({
    store,
    history,
    offerWebApi,
    errorPresenter,
  });
  const listOfferUsecase = new ListOfferUsecase({
    store,
    offerWebApi,
    errorPresenter,
  });

  const fatalErrorUsecase = new FatalErrorUsecase({ intercomService });

  const searchAssetUsecase = new SearchAssetUsecase({
    store,
    searchAssetApi,
    errorPresenter,
  });

  const dependency = {
    history,
    errorReporter,
    errorPresenter,
    messageDispatcher,
    authenticationUsecase,
    configurationChangingUsecase,
    showProjectUsecase,
    showOfferUsecase,
    offerApplicationUsecase,
    listOfferUsecase,
    fatalErrorUsecase,
    searchAssetUsecase,
    creationWebApi,
    creationSetWebApi,
    deliverableWebApi,
    fileUploader,
    modificationRequestWebApi,
    projectWebApi,
    filesWebApi,
    belfastClient,
    offer: {
      assignmentWebApi,
      offerWebApi,
    },
  };

  const container = document.getElementById('app');
  let isRendered = false;

  const update = (state: State) => {
    const element = createElement(Root, { dependency, history, state, config });

    if (!isRendered) {
      isRendered = true;

      render(element, container);

      return;
    }

    hydrate(element, container);
  };

  history.listen((location) => {
    // wait for document.title updates
    setTimeout(() => {
      execSitejs();
    });

    store.dispatch(setLocation(location));
  });

  store.addMiddleware(createKukerEmitterMiddleware());

  store.subscribe(() => update(store.getState()));

  update(store.getState());

  // set locale
  const language = configurationStorage.loadLanguage() || window.navigator.language;
  configurationChangingUsecase.changeLanguageTemporarily(language);

  execSitejs();
};

window.document.addEventListener('DOMContentLoaded', main);
